/**
 * Projection isométrique de la carte de Néopolis.
 *
 * La ville est posée sur une grille vue de trois quarts : une tuile carrée
 * devient un losange deux fois plus large que haut. C'est la projection des
 * jeux de gestion, et c'est elle qui donne du volume aux bâtiments — un plan
 * vu de dessus ne montre ni les façades, ni les hauteurs, ni les ombres.
 *
 * Conventions de la grille :
 *  - `gx` part vers la droite et vers le bas de l'écran ;
 *  - `gy` part vers la gauche et vers le bas ;
 *  - `hauteur` est en pixels, comptés vers le haut depuis le sol.
 *
 * Un volume montre donc trois faces : le dessus, la face qui regarde en bas à
 * gauche (arête W-S) et celle qui regarde en bas à droite (arête S-E).
 */

/** Demi-largeur d'une tuile, en pixels. */
export const DEMI_LARGEUR = 22;

/** Demi-hauteur d'une tuile. Moitié de la demi-largeur : projection 2:1. */
export const DEMI_HAUTEUR = 11;

/** Décalage du point (0, 0) de la grille dans le repère du SVG. */
const ORIGINE_X = 462;
const ORIGINE_Y = 76;

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** Place un point de la grille dans le repère du SVG. */
export function proj(gx: number, gy: number, hauteur = 0): Point {
  return {
    x: ORIGINE_X + (gx - gy) * DEMI_LARGEUR,
    y: ORIGINE_Y + (gx + gy) * DEMI_HAUTEUR - hauteur,
  };
}

/** Assemble des points en attribut `points` de polygone. */
export function pts(...points: readonly Point[]): string {
  return points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
}

/** Les quatre coins d'une empreinte rectangulaire, à une hauteur donnée. */
export function coins(
  gx: number,
  gy: number,
  l: number,
  p: number,
  hauteur = 0,
): { readonly n: Point; readonly e: Point; readonly s: Point; readonly o: Point } {
  return {
    n: proj(gx, gy, hauteur),
    e: proj(gx + l, gy, hauteur),
    s: proj(gx + l, gy + p, hauteur),
    o: proj(gx, gy + p, hauteur),
  };
}

/** Le losange d'une empreinte, prêt pour un `<polygon>`. */
export function losange(gx: number, gy: number, l: number, p: number, hauteur = 0): string {
  const c = coins(gx, gy, l, p, hauteur);
  return pts(c.n, c.e, c.s, c.o);
}

/**
 * Éclaircit (`facteur > 1`) ou assombrit (`facteur < 1`) une couleur `#rrggbb`.
 *
 * Les trois faces d'un volume viennent ainsi d'une seule teinte : c'est ce qui
 * fait lire le relief, et ça évite d'écrire trois couleurs cohérentes à la main
 * pour chaque bâtiment.
 */
export function teinter(couleur: string, facteur: number): string {
  const entier = Number.parseInt(couleur.slice(1), 16);
  const canaux = [(entier >> 16) & 255, (entier >> 8) & 255, entier & 255];
  const ajuste = canaux.map((c) => Math.max(0, Math.min(255, Math.round(c * facteur))));
  return `#${ajuste.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** Les trois éclairages d'un volume : dessus, face gauche, face droite. */
export const FACE_DESSUS = 1.0;
export const FACE_GAUCHE = 0.82;
export const FACE_DROITE = 0.64;
